import React, {createContext, useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'

const KlarnaContext = createContext()

const loadKlarnaScript = ({clientId, environment}) =>
    new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.async = true
        script.setAttribute('data-environment', environment)
        script.setAttribute('data-client-id', clientId)
        script.addEventListener(
            'load',
            () => {
                resolve()
            },
            {once: true}
        )
        script.addEventListener(
            'error',
            () => {
                reject(new Error('Failed to load Klarna script'))
            },
            {once: true}
        )
        script.src = 'https://js.klarna.com/web-sdk/v1/klarna.js'
        document.head.appendChild(script)
    })

const refreshPlacement = () => {
    global.Klarna?.OnsiteMessaging?.refresh?.()
}

export const KlarnaProvider = ({children, enabled, clientId, environment}) => {
    const [loadRequested, setLoadRequested] = useState(false)
    const options = useMemo(
        () => ({
            enabled: enabled && clientId && environment,
            clientId,
            environment
        }),
        [enabled, clientId, environment]
    )

    const loadKlarna = useCallback(() => {
        setLoadRequested(true)
        // refresh klarna placement in case it was already loaded
        refreshPlacement()
    }, [])

    const ctx = useMemo(() => {
        return {
            loadKlarna,
            options,
            refreshPlacement
        }
    }, [loadKlarna, options])

    useEffect(() => {
        if (loadRequested && options.enabled) {
            loadKlarnaScript(options)
        }
    }, [loadRequested, options])

    return <KlarnaContext.Provider value={ctx}>{children}</KlarnaContext.Provider>
}

KlarnaProvider.propTypes = {
    children: PropTypes.node,
    enabled: PropTypes.bool,
    clientId: PropTypes.string,
    environment: PropTypes.oneOf(['production', 'playground'])
}

export const useKlarna = () => {
    const ctx = React.useContext(KlarnaContext)

    if (!ctx) {
        throw new Error('useKlarna must be used within a KlarnaProvider')
    }

    return ctx
}
